import type { OptimizationGroupProductGroupMapping } from '@/shared/generated'
import type { Selection } from '@/shared/lib/forest'
import type { PickerForest } from './picker-forest'

import { Operators } from '@/shared/components/query-builder'
import { normalizeSelection } from '@/shared/lib/forest'

import { nodeId } from './node-id'
import { reportCatalogIssue } from './report'

const CATEGORY_FILTER = 'category_id'

const readCategoryIds = (
	entry: OptimizationGroupProductGroupMapping
): string[] | null => {
	const rule = entry.filters?.find((filter) => filter.name === CATEGORY_FILTER)
	if (!rule) {
		// A non-empty `filters` with no `category_id` rule holds some other rule
		// kind we don't support here. Treat it as nothing selected (visible
		// failure) rather than the whole PG (silent scope widening) — only a
		// genuinely empty/absent `filters` means "whole PG".
		if (entry.filters?.length) {
			reportCatalogIssue('unsupported_mapping_filters', {
				id: entry.product_group_id,
			})

			return []
		}

		return null
	}
	if (!('value' in rule) || !Array.isArray(rule.value)) {
		reportCatalogIssue('invalid_category_filter_value', {
			id: entry.product_group_id,
		})

		return []
	}

	return rule.value.map(String)
}

/**
 * Empty filters → the whole PG. Otherwise every id in the `category_id in`
 * filter → that category's subtree. Any node shape is accepted and coalesced.
 * Unknown PGs and categories are dropped and reported.
 */
export const selectionFromMapping = (
	pickerForest: PickerForest,
	mapping: OptimizationGroupProductGroupMapping[] | null | undefined
): Selection => {
	const ids: string[] = []
	for (const entry of mapping ?? []) {
		const pgNode = nodeId.pg(entry.product_group_id)
		if (!pickerForest.has(pgNode)) {
			reportCatalogIssue('unknown_product_group', {
				id: entry.product_group_id,
			})
			continue
		}
		const categoryIds = readCategoryIds(entry)
		if (categoryIds === null) {
			ids.push(pgNode)
			continue
		}
		for (const categoryId of categoryIds) {
			const node = nodeId.cat(entry.product_group_id, categoryId)
			if (pickerForest.has(node)) ids.push(node)
			else
				reportCatalogIssue('unknown_mapping_category', {
					productGroupId: entry.product_group_id,
					categoryId,
				})
		}
	}

	return normalizeSelection(pickerForest, ids)
}

/**
 * Groups selection roots by PG. A PG root → `{ filters: [] }`; category roots →
 * one `category_id in [...]` filter holding exactly those roots (already the
 * highest fully-selected nodes). Ordered by PG picker order, then category id.
 */
export const selectionToMapping = (
	pickerForest: PickerForest,
	selection: Selection
): OptimizationGroupProductGroupMapping[] => {
	const byPg = new Map<string, string[] | 'all'>()
	for (const root of selection.roots) {
		const { pgId, catId } = nodeId.parse(root)
		if (catId === undefined) {
			byPg.set(pgId, 'all')
			continue
		}
		const current = byPg.get(pgId)
		if (current === 'all') continue
		if (current) current.push(catId)
		else byPg.set(pgId, [catId])
	}

	const output: OptimizationGroupProductGroupMapping[] = []
	for (const pgNode of pickerForest.roots) {
		const { pgId } = nodeId.parse(pgNode)
		const value = byPg.get(pgId)
		if (!value) continue
		output.push(
			value === 'all'
				? { product_group_id: pgId, filters: [] }
				: {
						product_group_id: pgId,
						filters: [
							{
								name: CATEGORY_FILTER,
								operation: Operators.IN,
								value: [...value].sort(),
							},
						],
					}
		)
	}

	return output
}

/**
 * Rewrites an API mapping into the exact shape the picker emits (compact roots,
 * string ids, sorted). The picker rewrites the whole mapping on every toggle,
 * so a form hydrated with a non-canonical mapping stays dirty forever once
 * touched — even after the user undoes the change. Canonicalize the default and
 * check/uncheck round-trips back to it.
 */
export const canonicalizeMapping = (
	pickerForest: PickerForest,
	mapping: OptimizationGroupProductGroupMapping[] | null | undefined
): OptimizationGroupProductGroupMapping[] =>
	selectionToMapping(pickerForest, selectionFromMapping(pickerForest, mapping))
